import { z } from 'zod';
import { nullableMoneyMinorStringSchema } from './money';

export const propertyUnitTypesSchema = z
  .array(
    z.enum([
      'apartment',
      'house',
      'studio',
      'duplex',
      'triplex',
      'penthouse',
      'townhouse_unit',
      'office',
      'retail_local',
      'warehouse_unit',
      'coworking_space',
      'lot',
      'industrial_lot',
      'agricultural_parcel',
      'hotel_room',
      'suite',
      'condo_hotel_unit',
      'garage',
      'garage_with_storage',
      'storage_unit',
      'other',
    ])
  )
  .min(1)
  .max(10);

export const propertyAmenitySchema = z.enum([
  'luxury_finishes',
  'furnished',
  'dirt_road_access',
  'paved_road_access',
  'guard_booth',
  'nearby_malls',
  'electric_fence',
  'masonry_wall',
  'living_fence',
  'fireplace',
  'closet',
  'kitchen',
  'fitted_kitchen',
  'service_room',
  'duplex',
  'condominium',
  'nearby_schools',
  'oceanfront_first_row',
  'parkfront',
  'intercom',
  'jacuzzi',
  'garden',
  'nearby_parks',
  'pet_friendly',
  'pool',
  'reception',
  'security',
  'terrace',
  'triplex',
  'city_view',
  'ocean_view',
  'park_view',
  'walk_in_closet',
  'industrial_zone',
  'bbq_area',
  'air_conditioning',
  'laundry_area',
  'green_areas',
  'sports_courts',
  'access_control',
  'gym',
  'private_security',
  'white_goods',
  'grill',
  'lighting',
  'living_dining_room',
  'kids_playroom',
  'cleaning_service',
  'basic_services',
  'security_alarm',
  'commercial_use',
  'professional_use',
  'video_surveillance',
  'club_house',
  'guest_parking',
  'daily_dining_room',
  'sewer',
  'primary_bedroom_with_bathroom',
  'equipped',
  'entrance_hall',
  'independent_entrance',
  'internal_park',
  'patio',
  'communal_room',
  'entertainment_room',
  'sitting_room',
  'sauna',
  'solarium',
  'steam_room',
]);

const area = z
  .string()
  .max(12)
  .regex(/^\d+(?:\.\d{1,2})?$/u)
  .nullable();
export const propertyAlternativeSchema = z
  .object({
    unitId: z.uuid().nullable(),
    unitTypes: propertyUnitTypesSchema.nullable().optional(),
    unitCode: z.string().trim().min(1).max(160).nullable(),
    amenities: z.array(propertyAmenitySchema).min(1).max(40).nullable(),
    minPriceUsdMinor: nullableMoneyMinorStringSchema,
    maxPriceUsdMinor: nullableMoneyMinorStringSchema,
    minBathrooms: z.number().int().min(0).max(20).nullable(),
    minParkingSpaces: z.number().int().min(0).max(20).nullable(),
    typology: z
      .enum([
        'apartment',
        'flat',
        'studio',
        'duplex',
        'triplex',
        'penthouse',
        'loft',
      ])
      .nullable(),
    bedrooms: z.number().int().min(0).max(20).nullable(),
    minFloor: z.number().int().min(-10).max(200).nullable(),
    maxFloor: z.number().int().min(-10).max(200).nullable(),
    minAreaM2: area,
    maxAreaM2: area,
    minPricePenMinor: nullableMoneyMinorStringSchema,
    maxPricePenMinor: nullableMoneyMinorStringSchema,
    viviendaSocial: z.boolean().nullable(),
    sort: z.enum([
      'updated_desc',
      'price_pen_asc',
      'price_pen_desc',
      'area_desc',
      'floor_asc',
    ]),
    cursor: z.string().min(1).max(2_000).nullable(),
  })
  .strict();
export const propertySearchSchema = z
  .object({
    alternatives: z.array(propertyAlternativeSchema).min(1).max(5),
    unsupportedCriteria: z.array(z.string().min(1).max(500)).max(20),
  })
  .strict();
export type PropertySearch = z.infer<typeof propertySearchSchema>;

export function apartmentSearch(unitCode: string): PropertySearch {
  return {
    alternatives: [
      {
        unitId: null,
        unitCode,
        amenities: null,
        minPriceUsdMinor: null,
        maxPriceUsdMinor: null,
        minBathrooms: null,
        minParkingSpaces: null,
        typology: null,
        bedrooms: null,
        minFloor: null,
        maxFloor: null,
        minAreaM2: null,
        maxAreaM2: null,
        minPricePenMinor: null,
        maxPricePenMinor: null,
        viviendaSocial: null,
        sort: 'updated_desc',
        cursor: null,
      },
    ],
    unsupportedCriteria: [],
  };
}
